import { Component, computed, inject, input, output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  IntegrationRunStep,
  IntegrationRunStepKey,
  IntegrationRunStepStatus,
} from '../../../../../../shared/single-crawler/single-crawler.steps';
import { CrawlerStatus } from '../../../../../../models/integration/crawler';

const STEP_ICONS: Readonly<Record<IntegrationRunStepKey, string>> = {
  [IntegrationRunStepKey.DISCOVERY]: 'hub',
  [IntegrationRunStepKey.CRAWL]: 'travel_explore',
  [IntegrationRunStepKey.ANALYZE]: 'insights',
};

const NEXT_ACTION_KEYS: Readonly<Record<IntegrationRunStepKey, string>> = {
  [IntegrationRunStepKey.DISCOVERY]: 'INTEGRATIONS.STEPS.NEXT_DISCOVERY',
  [IntegrationRunStepKey.CRAWL]: 'INTEGRATIONS.STEPS.NEXT_CRAWL',
  [IntegrationRunStepKey.ANALYZE]: 'INTEGRATIONS.STEPS.NEXT_ANALYZE',
};

const STEP_DESC_KEYS: Readonly<Record<IntegrationRunStepKey, string>> = {
  [IntegrationRunStepKey.DISCOVERY]: 'INTEGRATIONS.STEPS.DISCOVERY_DESC',
  [IntegrationRunStepKey.CRAWL]: 'INTEGRATIONS.STEPS.CRAWL_DESC',
  [IntegrationRunStepKey.ANALYZE]: 'INTEGRATIONS.STEPS.ANALYZE_DESC',
};

// discovery is never locked
const STEP_ACCESS_KEYS: Readonly<Record<Exclude<IntegrationRunStepKey, IntegrationRunStepKey.DISCOVERY>, string>> = {
  [IntegrationRunStepKey.CRAWL]: 'INTEGRATIONS.STEPS.DISCOVERY_ACCESS',
  [IntegrationRunStepKey.ANALYZE]: 'INTEGRATIONS.STEPS.CRAWL_ACCESS',
};

/**
 * Renders the Discovery -> Crawl -> Analyze stepper, or just Crawl -> Analyze
 * when not of discovery type. See `STEPS_BY_CRAWLER_TYPE`.
 */
@Component({
  selector: 'app-single-crawler-stepper',
  templateUrl: './single-crawler-stepper.component.html',
  styleUrls: ['./single-crawler-stepper.component.scss'],
})
export class SingleCrawlerStepperComponent {
  private readonly translate = inject(TranslateService);

  readonly steps = input<IntegrationRunStep[]>([]);
  readonly activeIndex = input(0);
  readonly liveStatus = input<CrawlerStatus>();
  readonly stepSelected = output<IntegrationRunStep>();

  readonly runStepStatus = IntegrationRunStepStatus;

  // The step whose work runs now, if any.
  readonly runningStep = computed(() => this.steps().find((step) => this.isStepActive(step)));

  // The step to do next: the running one, or the first one open and not done yet.
  readonly nextStep = computed(() => this.runningStep()
    ?? this.steps().find((step) => step.selectable && step.status !== IntegrationRunStepStatus.COMPLETED));

  stateKey(step: IntegrationRunStep): string {
    if (this.isStepActive(step)) return 'INTEGRATIONS.STEPS.STATE_RUNNING';
    if (this.isStepDone(step)) return 'INTEGRATIONS.STEPS.STATE_DONE';
    return step.selectable ? 'INTEGRATIONS.STEPS.STATE_READY' : 'INTEGRATIONS.STEPS.STATE_LOCKED';
  }

  nextActionKey(step: IntegrationRunStep): string {
    return NEXT_ACTION_KEYS[step.key];
  }

  iconFor(step: IntegrationRunStep): string {
    return STEP_ICONS[step.key] ?? 'circle';
  }

  select(step: IntegrationRunStep): void {
    if (!step.selectable) return;
    this.stepSelected.emit(step);
  }

  // desc of step + unlock condition if step currently locked/disabled
  tooltipFor(step: IntegrationRunStep): string {
    const description = this.translate.instant(STEP_DESC_KEYS[step.key]);
    if (step.selectable || step.key === IntegrationRunStepKey.DISCOVERY) {
      return description;
    }
    const access = this.translate.instant(STEP_ACCESS_KEYS[step.key]);
    return `${description}\n${access}`;
  }

  // A step still running is not done yet, even once it produced a first result.
  isStepDone(step: IntegrationRunStep): boolean {
    return step.status === IntegrationRunStepStatus.COMPLETED && !this.isStepActive(step);
  }

  isStepActive(step: IntegrationRunStep): boolean {
    const status = this.liveStatus();
    if (!status) return false;
    switch (step.key) {
      case IntegrationRunStepKey.DISCOVERY:
        return status === CrawlerStatus.DISCOVERING;
      case IntegrationRunStepKey.CRAWL:
        return [
          CrawlerStatus.CRAWLING,
          CrawlerStatus.UPLOADING,
        ].includes(status);
      default:
        return false;
    }
  }
}
